import { useState, useEffect } from "react";
import { Plus, Loader2, Send, Trash2, Clock, Calendar, X, Play, CheckCircle, AlertCircle, Hourglass, ChevronDown, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import API from "@/lib/api";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  scheduled: "bg-amber-50 text-amber-700 border-amber-200",
  running: "bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partially_completed: "bg-orange-50 text-orange-700 border-orange-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-400 border-slate-200",
};

const STATUS_ICONS = {
  draft: Hourglass,
  scheduled: Clock,
  running: Loader2,
  completed: CheckCircle,
  partially_completed: AlertCircle,
  failed: AlertCircle,
  cancelled: X,
};

function formatScheduledAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });
}

function getSmsInfo(message) {
  const GSM7 = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà\^{}\\\[~\]|€]*$/;
  const isGsm = GSM7.test(message);
  const len = isGsm ? [...message].reduce((a, c) => a + ("^{}\\[~]|€".includes(c) ? 2 : 1), 0) : message.length;
  const sl = isGsm ? 160 : 70;
  const ml = isGsm ? 153 : 67;
  const segs = len === 0 ? 0 : len <= sl ? 1 : Math.ceil(len / ml);
  return { encoding: isGsm ? "GSM-7" : "Unicode", charCount: len, segments: segs };
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [senderIds, setSenderIds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [creditsPerSegment, setCreditsPerSegment] = useState(1);
  const [wallet, setWallet] = useState(null);
  const [form, setForm] = useState({
    name: "", sender_id: "", message: "", group_ids: [], scheduled_at: ""
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [campsRes, sidRes, grpsRes, costRes, walletRes] = await Promise.all([
        API.get("/campaigns"),
        API.get("/sender-ids"),
        API.get("/contacts/groups"),
        API.get("/sms/cost-info"),
        API.get("/wallet/balance"),
      ]);
      setCampaigns(campsRes.data.campaigns || []);
      setSenderIds((sidRes.data?.sender_ids || []).filter(s => s.status === "approved"));
      setGroups(grpsRes.data?.groups || []);
      setCreditsPerSegment(costRes.data?.credits_per_segment || 1);
      setWallet(walletRes.data);
    } catch { toast.error("Failed to load campaigns"); }
    finally { setLoading(false); }
  };

  const createCampaign = async () => {
    if (!form.name || !form.sender_id || !form.message) {
      toast.error("Name, sender ID and message are required");
      return;
    }
    if (form.group_ids.length === 0) {
      toast.error("Select at least one contact group");
      return;
    }
    if (scheduleMode && !form.scheduled_at) {
      toast.error("Please select a date and time to schedule");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        sender_id: form.sender_id,
        message: form.message,
        group_ids: form.group_ids,
        schedule_at: scheduleMode ? new Date(form.scheduled_at).toISOString() : null,
      };
      const res = await API.post("/campaigns", payload);
      toast.success(scheduleMode ? `Campaign scheduled for ${formatScheduledAt(payload.schedule_at)}` : "Campaign created as draft");
      setShowCreate(false);
      setForm({ name: "", sender_id: "", message: "", group_ids: [], scheduled_at: "" });
      setScheduleMode(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create campaign"); }
    finally { setSaving(false); }
  };

  const sendCampaign = async (campaign_id) => {
    if (!window.confirm("Send this campaign now? Credits will be deducted.")) return;
    try {
      await API.post(`/campaigns/${campaign_id}/send`);
      toast.success("Campaign sending started");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send campaign"); }
  };

  const cancelCampaign = async (campaign_id) => {
    if (!window.confirm("Cancel this campaign?")) return;
    try {
      await API.delete(`/campaigns/${campaign_id}`);
      toast.success("Campaign cancelled");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot cancel campaign"); }
  };

  const toggleGroup = (gid) => {
    setForm(f => ({
      ...f,
      group_ids: f.group_ids.includes(gid) ? f.group_ids.filter(x => x !== gid) : [...f.group_ids, gid]
    }));
  };

  const smsInfo = getSmsInfo(form.message);
  const totalSelectedRecipients = groups
    .filter(g => form.group_ids.includes(g._id))
    .reduce((acc, g) => acc + (g.contact_count || 0), 0);
  const estimatedCredits = smsInfo.segments * creditsPerSegment * totalSelectedRecipients;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Campaigns</h1>
          <p className="text-sm text-slate-500 mt-1">Send bulk SMS to contact groups — instantly or at a scheduled time</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="create-campaign-btn">
          <Plus size={15} className="mr-2" /> New Campaign
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-4">New Campaign</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-700">Campaign Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. June Promo Blast" className="h-9 text-sm" data-testid="campaign-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-700">Sender ID *</Label>
              <select value={form.sender_id} onChange={e => setForm({ ...form, sender_id: e.target.value })}
                className="w-full h-9 border border-slate-300 rounded-md px-3 text-sm text-slate-900 bg-white focus:outline-none focus:border-[#0c1e41]" data-testid="campaign-sender-id">
                <option value="">Select Sender ID</option>
                {senderIds.map(s => <option key={s._id} value={s.sender_id}>{s.sender_id}</option>)}
              </select>
            </div>
          </div>

          {/* Contact groups */}
          <div className="mt-4">
            <Label className="text-xs text-slate-700 mb-2 block">Contact Groups * <span className="font-normal text-slate-400">(select one or more)</span></Label>
            {groups.length === 0 ? (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">No contact groups. Create groups in Contacts first.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {groups.map(g => (
                  <button key={g._id} type="button" onClick={() => toggleGroup(g._id)}
                    className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${form.group_ids.includes(g._id) ? "bg-[#0c1e41] text-white border-[#0c1e41]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0c1e41]"}`}
                    data-testid={`group-toggle-${g._id}`}>
                    {g.name} ({g.contact_count || 0})
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Message */}
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs text-slate-700">Message *</Label>
            <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={4}
              placeholder="Type your SMS message here…"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 bg-white resize-none focus:outline-none focus:border-[#0c1e41] placeholder:text-slate-400"
              data-testid="campaign-message-input" />
            <p className="text-xs text-slate-400 flex gap-3">
              <span>{smsInfo.charCount}/160 characters</span>
              <span>{smsInfo.segments || 1} segment{(smsInfo.segments || 1) > 1 ? "s" : ""}</span>
              <span className="text-[#0c1e41] font-medium">({creditsPerSegment} credit{creditsPerSegment > 1 ? "s" : ""}/segment)</span>
            </p>
          </div>

          {/* Credit Estimate Panel */}
          {form.message.trim() && form.group_ids.length > 0 && (
            <div className="mt-1 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-4" data-testid="campaign-credit-estimate">
              <div className="flex items-center gap-2 mb-3">
                <Info size={14} className="text-[#0c1e41]" />
                <span className="text-xs font-bold text-[#0c1e41]">Credit Estimate</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Segments/msg", value: smsInfo.segments || 1 },
                  { label: "Credits/segment", value: creditsPerSegment },
                  { label: "Recipients", value: totalSelectedRecipients.toLocaleString() },
                  { label: "Est. Total Credits", value: estimatedCredits.toLocaleString(), highlight: true },
                ].map(row => (
                  <div key={row.label} className="bg-white border border-[#c0cfe8]/60 rounded-lg px-3 py-2 text-center">
                    <p className="text-[10px] text-slate-500 mb-0.5">{row.label}</p>
                    <p className={`text-sm font-bold font-mono ${row.highlight ? "text-[#0c1e41]" : "text-slate-800"}`}>{row.value}</p>
                  </div>
                ))}
              </div>
              {wallet && estimatedCredits > wallet.balance && (
                <p className="text-xs text-red-600 mt-2 font-medium">
                  Insufficient credits — you have {wallet.balance.toLocaleString()}, need {estimatedCredits.toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Schedule toggle */}
          <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setScheduleMode(!scheduleMode)}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors ${scheduleMode ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
              data-testid="schedule-toggle">
              <div className="flex items-center gap-2">
                <Calendar size={15} />
                <span>{scheduleMode ? "Scheduled send" : "Schedule for later (optional)"}</span>
              </div>
              <ChevronDown size={14} className={`transition-transform ${scheduleMode ? "rotate-180" : ""}`} />
            </button>
            {scheduleMode && (
              <div className="px-4 py-3 border-t border-slate-200 bg-white">
                <Label className="text-xs text-slate-700 mb-1.5 block">Send Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={form.scheduled_at}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  onChange={e => setForm({ ...form, scheduled_at: e.target.value })}
                  className="h-9 text-sm w-full sm:w-auto"
                  data-testid="schedule-datetime-input"
                />
                <p className="text-xs text-slate-400 mt-1.5">Campaign will send automatically at the scheduled time.</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-4">
            <Button onClick={createCampaign} disabled={saving} className={`text-sm shadow-sm ${scheduleMode ? "bg-amber-600 hover:bg-amber-700" : "bg-[#0c1e41] hover:bg-[#091629]"}`} data-testid="save-campaign-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : scheduleMode ? <Clock size={14} className="mr-2" /> : <Plus size={14} className="mr-2" />}
              {scheduleMode ? "Schedule Campaign" : "Create Campaign"}
            </Button>
            <Button variant="outline" onClick={() => { setShowCreate(false); setScheduleMode(false); }} className="text-sm">Cancel</Button>
          </div>
        </div>
      )}

      {/* Campaign list */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 size={24} className="animate-spin text-[#0c1e41]" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <Send size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">No campaigns yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first bulk SMS campaign above</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => {
            const StatusIcon = STATUS_ICONS[c.status] || Clock;
            const isScheduled = c.status === "scheduled";
            const isDraft = c.status === "draft";
            const canSend = isDraft || isScheduled;
            const canCancel = canSend;
            return (
              <div key={c._id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`campaign-card-${c.campaign_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-slate-900 text-sm">{c.name}</p>
                      <Badge variant="outline" className={`text-xs capitalize ${STATUS_STYLES[c.status] || "bg-slate-100 text-slate-600"}`}>
                        {c.status === "running" ? <Loader2 size={10} className="animate-spin mr-1" /> : null}
                        {c.status?.replace("_", " ")}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-1 mb-1.5">{c.message}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="font-mono font-semibold text-[#0c1e41] bg-[#eef1f8] border border-[#c0cfe8] px-2 py-0.5 rounded">{c.sender_id}</span>
                      <span>{(c.total_recipients || 0).toLocaleString()} recipients</span>
                      {c.estimated_credits > 0 && <span>~{c.estimated_credits} credits</span>}
                      {c.completed_at && <span>Sent {new Date(c.completed_at).toLocaleDateString("en-NG", { dateStyle: "short" })}</span>}
                    </div>
                    {/* Schedule info */}
                    {isScheduled && c.scheduled_at && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 w-fit">
                        <Clock size={11} />
                        <span>Scheduled for <strong>{formatScheduledAt(c.scheduled_at)}</strong></span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canSend && (
                      <Button size="sm" onClick={() => sendCampaign(c.campaign_id)}
                        className="bg-[#0c1e41] hover:bg-[#091629] h-8 text-xs shadow-sm" data-testid={`send-campaign-${c.campaign_id}`}>
                        <Play size={12} className="mr-1" /> Send Now
                      </Button>
                    )}
                    {canCancel && (
                      <button onClick={() => cancelCampaign(c.campaign_id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`cancel-campaign-${c.campaign_id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
